// @flow
import styled, { css } from 'styled-components';
import { grid } from '@finect/tabular-utils/grid';
import { minMediaQueries, maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { getFont, getLineHeight } from '@finect/tabular-utils/getFont';
import { textEllipsis } from '@finect/tabular-utils/textEllipsis';
import { getImageUrl } from '@finect/tabular-components/Image';
import { ToolbarContent, Action } from './components/Toolbar';

const PHONE_IMAGE_MIN_HEIGHT = 250;
const DESKTOP_IMAGE_MAX_HEIGHT = 240;

export const QuestionLabel: any = styled.span``;

const commonStyles = css`
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  width: ${({ size }) => size || '84px'};
  height: ${({ size }) => size || '84px'};
  border-radius: 2px;
  margin: ${({ margin }) => margin || '0 0 0 15px'};
`;

export const SquareImage: any = styled.div`
  ${commonStyles}
  box-shadow: 2px 2px 5px 1px rgb(58 38 136 / 0.3);

  ${minMediaQueries.phone`
    margin-right: -15px;
    ${({ disableMoveImage }) => (disableMoveImage ? '' : 'margin-top: -30px;')};
  `}

  img {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    color: ${({ theme }) => theme.colors.primary};
    transform: translate(-50%, -50%);
  }
`;

export const SquareIcon: any = styled.div`
  ${commonStyles}
  background-color: ${({ theme }) => theme.colors.uiLL};
  color: ${({ theme }) => theme.colors.primary};

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

// ----------------------------- VIDEO -----------------------------
/* eslint-disable */
export const VideoContainer: any = styled.div`
  display: flex;
  flex-direction: column;
  padding: 36px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.uiM};

  ${/* sc-sel */ ToolbarContent} {
    display: block;
  }

  ${/* sc-sel */ Action} {
    &:not(:last-of-type) {
      margin-bottom: 12px;
    }
  }

  > div:not(:first-of-type) {
    display: flex;
    max-width: none;

    > div:first-child {
      flex-shrink: 1;
      width: 100%;
      max-width: ${grid(3)}px;
      margin-right: 18px;
    }

    > div:last-child {
      flex-shrink: 2;
      width: 100%;
      max-width: ${grid(7)}px;
    }

    ${maxMediaQueries.miniDesktop`
      h3 {
        ${getFont(2, 'compact')}
      }

      ${/* sc-sel */ ToolbarContent} {
        display: flex;
        margin-top: 0;
      }

      ${/* sc-sel */ Action} {
        &:not(:last-of-type) {
          margin-bottom: 6px;
        }
      }
    `}

    ${maxMediaQueries.phablet`
      position: relative;
      flex-wrap: wrap;

      > div:first-child {
        margin-right: 0;
      }
        
      > div:last-child {
        max-width: none;
        margin-top: 18px;
      }
          
      h3 {
        ${getFont(1, 'xcompact')}
      }
    `}
  }
`;
/* eslint-enable */

export const VideoContent: any = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: fit-content;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgb(46 46 51 / 0.25);
  cursor: pointer;
`;

export const VideoIframe: any = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

// ----------------------------- ARTICLE -----------------------------

export const Author: any = styled.div`
  width: auto;
  margin-top: 6px;
  ${getFont(-2, 'compact')}
  font-weight: 700;

  a,
  span {
    display: flex;
    flex-direction: row;
    align-items: center;
    color: currentColor;
  }

  .author {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;
  }
`;

/* eslint-disable */
const commonContentStyles = css`
  display: flex;

  h3 {
    ${maxMediaQueries.miniDesktop`
      ${getFont(2, 'compact')}
    `}

    ${maxMediaQueries.phone`
      ${getFont(1, 'xcompact')}
    `}

    a {
      color: currentColor;
    }
  }

  ${maxMediaQueries.phone`
    position: relative;
    padding-bottom: 60px;
  `}

  ${/* sc-sel */ SquareImage} {
    ${maxMediaQueries.miniDesktop`
      width: 114px;
      height: 114px;
    `}

    ${maxMediaQueries.phone`
      width: 84px;
      height: 84px;
    `}
  }

  ${/* sc-sel */ SquareIcon} {
    ${maxMediaQueries.miniDesktop`
      width: 114px;
      height: 114px;

      svg {
        width: 60px;
        height: 60px;
        stroke-width: 3.8;
      }
    `}

    ${maxMediaQueries.phone`
      display: none;
    `}
  }
`;

export const ArticleContent: any = styled.div`
  ${commonContentStyles};
  justify-content: space-between;
  padding: 24px 0;
  ${({ noMarginTop }) => (noMarginTop ? 'padding: 0;' : 'padding: 24px 0;')}
  ${({ noBorderTop, theme }) => (noBorderTop ? 'border-top: none;' : `border-top: 1px solid ${theme.colors.uiM};`)}
  ${maxMediaQueries.phablet`
    ${({ noMarginTop }) => (noMarginTop ? 'padding: 0 0 18px;' : 'padding: 18px 0;')}
  `}

  &:first-of-type {
    border-top: none;
    ${maxMediaQueries.phablet`
      ${({ noMarginTop }) => (noMarginTop ? 'padding: 0 0 48px;' : 'padding: 12px 0 48px;')}
    `}
  }

  h3 {
    padding-bottom: 6px;

    span {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  small {
    strong {
      margin-right: 6px;
    }

    span {
      margin-right: 6px;
    }
  }
`;

export const ArticleCardContainer: any = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${({ simple }) =>
    simple &&
    css`
      background-color: ${({ theme }) => theme.plus.grey2};
      border-bottom: 2px solid #fff;
      padding: 20px;

      ${ArticleContent} {
        padding: 0;

        h3 {
          ${getFont(1.5, 'compact')}
          line-height: ${getLineHeight(0.25, 'xcompact')};
          font-weight: 500;
          padding-bottom: 12px;
        }

        p {
          color: ${({ theme }) => theme.plus.fontD};
          ${getFont(-1, 'compact')}
          line-height: ${getLineHeight(0.5, 'compact')};
          font-weight: 400;
        }
      }
    `}
`;

export const PlusImage: any = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-grow: 1;
  align-items: flex-start;
  height: 100%;
  max-height: ${DESKTOP_IMAGE_MAX_HEIGHT}px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    overflow: hidden;
  }
`;

export const ArticleInfo: any = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  max-width: ${({ hasImage }) => (hasImage ? '80%' : `${grid(12)}px`)};
  padding-right: ${({ hasImage }) => (hasImage ? `30px` : ``)};

  .date-external {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;

    a {
      margin-left: 8px;
      vertical-align: middle;
    }
  }

  ${SquareImage} {
    position: absolute;
    top: 50px;
    right: 20px;
  }

  ${maxMediaQueries.phone`
    max-width: ${({ hasImage }) => (hasImage ? `${grid(8)}px` : `${grid(12)}px`)};

    .date-external {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    ${SquareImage} {
      position: relative;
      top: 0;
      right: 0;
    }

    ${({ hasImage }) =>
      hasImage &&
      css`
        padding-right: 5px;

        .title-image {
          display: flex;
          flex-direction: row;
          align-items: start;
          justify-content: space-between;
        }
      `}
  `}
`;

export const ArticlePlusInfo: any = styled.div`
  display: grid;
  width: 100%;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.plus.grey};

  grid-template-columns: 300px 2fr;
  ${minMediaQueries.miniDesktop`grid-template-columns: 400px 2fr;`}

  > :last-child {
    border-left: 5px solid ${({ theme }) => theme.plus.primary};
    padding: 16px 20px;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
  }

  .tags-title-mobile,
  .overlay {
    ${({ hasVideo }) => !hasVideo && 'display: none;'}
  }

  .tags-fav-desktop,
  .tags-fav-mobile {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    > :last-child {
      margin: 0;
    }
  }

  ${VideoContent} {
    height: 100%;
    width: 100%;
    box-shadow: none;
    border-radius: 0;
    max-height: ${DESKTOP_IMAGE_MAX_HEIGHT}px;
    min-height: 100%;

    > div,
    > div > div {
      height: 100%;

      img {
        object-fit: cover;
        border-radius: 0;
      }
    }
  }

  ${PlusImage} {
    border-radius: 5px 0 0 5px;
    min-height: 100%;
  }

  ${Author} {
    margin-right: 12px;
  }

  .author-date {
    white-space: nowrap;
    position: relative;
    flex-wrap: wrap;
  }

  .date-external {
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-end;
    align-items: left;
    flex-wrap: wrap;

    small,
    a,
    span {
      margin-left: 0;
      padding-left: 0;
    }
  }

  ${maxMediaQueries.miniDesktop`
    h3 {
      ${getFont(1, 'compact')}
    }

    p {
      ${getFont(-1, 'compact')}
    }
  `}

  ${maxMediaQueries.phablet`
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    max-height: none;
    border-radius: 12px;
    position: relative;
    overflow: hidden;
    max-width: 100%;

    
    > :last-child {
      max-height: none;
      border-left: none;
      padding: 10px;
    }

    .date-external {
      flex-direction: column;
      align-items: flex-start;
    }
    
    ${PlusImage} {
      min-height: ${PHONE_IMAGE_MIN_HEIGHT}px;
      
      .overlay {
        ${({ hasVideo }) => !hasVideo && 'display: block;'}
        background: linear-gradient(to top, rgba(0, 0, 0, 0.5) 30%, rgba(0, 0, 0, 0.2) 70%, rgba(0, 0, 0, 0) 100%);
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      img {
        border-radius: 12px 12px 0 0;
      }
    }

    ${/* sc-sel */ VideoContent} {
      max-height: ${PHONE_IMAGE_MIN_HEIGHT}px !important;
      
      img {
        border-radius: 12px 12px 0 0 !important;
      }
    }

    .tags-title-desktop {
      ${({ hasVideo }) => !hasVideo && 'display: none;'}
    }

    .tags-title-mobile {
      ${({ hasVideo }) => !hasVideo && 'position: absolute;'}
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 10px;
      justify-content: space-between;
    }

    h3 {
      ${getFont(2, 'compact')}
      font-weight: 400;
      ${({ hasVideo }) => !hasVideo && ' color: #fff; padding-bottom: 0;'} 
    }

    p {
      ${getFont(0, 'compact')}
      font-weight: 300;
      color: ${({ theme }) => theme.plus.fontD};
      margin-bottom: 24px;
    }
  `}
`;

export const ArticlePlusBInfo: any = styled.div`
  position: relative;
  border-radius: 5px;
  width: 100%;
  max-height: ${DESKTOP_IMAGE_MAX_HEIGHT * 2}px;
  background-color: ${({ theme }) => theme.plus.grey};

  color: #fff;

  h3 {
    ${getFont(3, 'compact')}
    font-weight: 400;
  }

  p,
  .author,
  a {
    color: #f1f1f1;
  }

  time {
    color: #c3c3c3;
  }

  > div {
    height: 100%;
  }

  > :last-child {
    max-height: ${DESKTOP_IMAGE_MAX_HEIGHT * 2}px;
    border-radius: 0 0 5px 5px;
    padding: 16px 20px;

    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 2;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;

    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.7) 40%,
      rgba(0, 0, 0, 0.35) 60%,
      rgba(0, 0, 0, 0.1) 80%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  .tags-title-mobile,
  .overlay {
    ${({ hasVideo }) => !hasVideo && 'display: none;'}
  }

  ${VideoContent} {
    height: 100%;
    width: 100%;
    box-shadow: none;
    border-radius: 5px;
    max-height: ${DESKTOP_IMAGE_MAX_HEIGHT * 2}px;

    > div,
    > div > div {
      height: 100%;

      img {
        object-fit: cover;
        border-radius: 5px;
      }
    }
  }

  ${PlusImage} {
    max-height: ${DESKTOP_IMAGE_MAX_HEIGHT * 2}px;
    border-radius: 5px;
  }

  .author-date {
    border-top: 1px solid rgba(255, 255, 255, 0.85);
    padding-top: 12px;
    margin-top: 12px;
  }

  ${Author} {
    margin-right: 12px;
  }

  .date-external {
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-end;
    align-items: left;
    flex-wrap: wrap;

    small,
    a,
    span {
      margin-left: 0;
      padding-left: 0;
    }
  }

  ${maxMediaQueries.miniDesktop`
    h3 {
      ${getFont(1, 'compact')}
    }

    p {
      ${getFont(-1, 'compact')}
    }
  `}

  ${maxMediaQueries.phablet`
    max-height: none;
    border-radius: 12px;
    position: relative;
    overflow: hidden;
    max-width: 100%;

    
    > :last-child {
      max-height: none;
      border-left: none;
      padding: 10px;
    }

    .date-external {
      flex-direction: column;
      align-items: flex-start;
    }
    
    ${PlusImage} {
      min-height: ${PHONE_IMAGE_MIN_HEIGHT * 1.5}px;
      max-height: ${PHONE_IMAGE_MIN_HEIGHT * 2}px;
      
      .overlay {
        ${({ hasVideo }) => !hasVideo && 'display: block;'}
        background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 30%, rgba(0, 0, 0, 0.4) 70%, rgba(0, 0, 0, 0) 90%);
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      img {
        border-radius: 12px 12px 0 0;
      }
    }

    ${/* sc-sel */ VideoContent} {
      min-height: ${PHONE_IMAGE_MIN_HEIGHT * 1.5}px !important;
      max-height: ${PHONE_IMAGE_MIN_HEIGHT * 2}px !important;
      
      img {
        border-radius: 12px 12px 0 0 !important;
      }
    }

    h3 {
      ${getFont(2, 'compact')}
      font-weight: 400;
      ${({ hasVideo }) => !hasVideo && 'color: #fff; padding-bottom: 0;'} 
    }

    p {
      ${getFont('-1', 'compact')}
      font-weight: 300;
      color: #fff;
    }
  `}
`;

export const Small: any = styled.small`
  display: block;
  margin: 5px 0 0 ${({ withLeftMargin = true }) => (withLeftMargin ? '8px' : '0')};
  ${getFont(-1, 'compact')}
  color: ${({ theme }) => theme.colors.terciary};
`;

export const External: any = styled.a`
  display: inline-block;
  position: relative;
  overflow: hidden;
  max-width: 320px;
  border-radius: 18px;
  margin-right: 9px;
  color: ${({ theme }) => theme.colors.fontL};
  ${textEllipsis};

  span {
    vertical-align: middle;
    ${getFont(-1, 'compact')}
  }

  svg {
    margin-right: 4px;
    vertical-align: middle;
  }
`;

export const ExternalImage: any = styled.div`
  width: 100%;
  height: 120px;
  box-shadow: 2px 2px 5px 1px rgb(60 68 215 / 0.3);
  margin-bottom: 20px;
  background-image: ${({ src }) => `url(${getImageUrl(src)})`};
  background-position: center;
  background-size: cover;
`;

export const PublishDate: any = styled(Small)`
  ${getFont(-2, 'compact')}
  color: ${({ theme }) => theme.colors.terciary};
`;
